import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import MTLogo from "./MTLogo";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/destinations", label: "Destinations" },
  { to: "/experiences", label: "Experiences" },
  { to: "/about", label: "About" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-ink/80 backdrop-blur-md border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-editorial flex items-center justify-between h-20 md:h-24">
        <Link to="/" className="flex items-center gap-4 group" data-testid="header-logo">
          <MTLogo size={36} />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif text-lg text-bone tracking-tight">Mensorio</div>
            <div className="overline text-[10px] mt-0.5">Private Japan</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10" data-testid="primary-nav">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `link-underline text-[13px] tracking-[0.18em] uppercase font-light text-bone/75 hover:text-bone transition-colors duration-300 ${
                  isActive ? "is-active text-bone" : ""
                }`
              }
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/plan"
            className="text-[12px] tracking-[0.24em] uppercase font-normal border border-gold/60 text-bone px-5 py-3 hover:bg-gold hover:text-ink transition-colors duration-500"
            data-testid="header-plan-journey-btn"
          >
            Plan Your Journey
          </Link>
        </nav>

        <button
          className="md:hidden text-bone p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-500 bg-ink/95 backdrop-blur-lg ${
          open ? "max-h-[480px] opacity-100 border-t border-white/5" : "max-h-0 opacity-0"
        }`}
        data-testid="mobile-menu"
      >
        <div className="container-editorial py-8 flex flex-col gap-6">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-[14px] tracking-[0.2em] uppercase font-light text-bone/80 ${isActive ? "text-gold" : ""}`
              }
              data-testid={`nav-mobile-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/plan"
            className="mt-2 inline-block text-[12px] tracking-[0.24em] uppercase border border-gold/60 text-bone px-5 py-3 text-center hover:bg-gold hover:text-ink transition-colors"
            data-testid="mobile-plan-journey-btn"
          >
            Plan Your Journey
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
